import { BsCrop } from 'react-icons/bs';
import { MdColorLens, MdFlip, MdRotateLeft, MdRotateRight, MdVerticalAlignCenter } from 'react-icons/md';
import { cn } from '@/core/ui';
import { Button } from '@/core/ui/Form';
import { useImage } from '@/store/image';

const DEFAULT_COLORS = [
  { name: 'Transparent', value: 'transparent' },
  { name: 'Black', value: '#000000' },
  { name: 'White', value: '#ffffff' },
  // { name: 'Red', value: '#ff0000' },
];

export const ImageTransformControls = () => {
  const backgroundColor = useImage.use.backgroundColor();
  const customColor = useImage.use.customColor();
  const onBackgroundColorChange = useImage.use.setBackgroundColor();
  const onCustomColorChange = useImage.use.setCustomColor();
  const isCropping = useImage.use.isCropping();
  const setIsCropping = useImage.use.setIsCropping();
  const controls = useImage.use.controls();
  const rotateLeft = useImage.use.rotateLeft();
  const rotateRight = useImage.use.rotateRight();
  const flipH = useImage.use.flipH();
  const flipV = useImage.use.flipV();
  const onCropApply = useImage.use.onCropApply();
  // const settings = controls?.getSettings() ?? {};
  // const absoluteZoom = getAbsoluteZoom(controls?.getState() ?? null, settings as ExtendedSettings);

  const flip = (horizontal: boolean, vertical: boolean) => {
    if (horizontal) {
      flipH();
    } else if (vertical) {
      flipV();
    }
    if (controls) {
      controls.flipImage(horizontal, vertical);
    }
  };

  const rotate = (angle: number) => {
    if (angle === -90) {
      rotateLeft();
    } else if (angle === 90) {
      rotateRight();
    }
    if (controls) {
      controls.rotateImage(angle);
    }
  };
  // const onZoom = (value: number, transitions?: boolean) => {
  //   if (controls) {
  //     controls.zoomImage(getZoomFactor(controls?.getState() ?? null, settings as ExtendedSettings, value), {
  //       transitions: !!transitions,
  //     });
  //   }
  // };

  // const onZoomIn = () => {
  //   if (controls && isNumber(absoluteZoom)) {
  //     onZoom(Math.min(1, absoluteZoom + 0.25), true);
  //   }
  // };

  // const onZoomOut = () => {
  //   if (controls && isNumber(absoluteZoom)) {
  //     onZoom(Math.max(0, absoluteZoom - 0.25), true);
  //   }
  // };

  return (
    <div className='space-y-6 border-t border-default-200'>
      <div>
        <h4 className='text-sm font-medium my-3'>Background Color</h4>
        <div className='space-y-3'>
          <div className='flex gap-2'>
            {DEFAULT_COLORS.map((color) => (
              <button
                key={color.value}
                type='button'
                onClick={() => onBackgroundColorChange(color.value)}
                className={cn('size-8 rounded border-4', {
                  'border-warning': backgroundColor === color.value,
                  'border-default-300': backgroundColor !== color.value,
                })}
                style={{
                  backgroundColor: color.value,
                  backgroundImage:
                    color.value === 'transparent'
                      ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                      : undefined,
                  backgroundSize: color.value === 'transparent' ? '8px 8px' : undefined,
                  backgroundPosition:
                    color.value === 'transparent' ? '0 0, 0 4px, 4px -4px, -4px 0px' : undefined,
                }}
                title={color.name}
              />
            ))}

            <div className='flex items-center gap-2'>
              <input
                title='Custom Color'
                type='color'
                value={customColor}
                onChange={(e) => onCustomColorChange(e.target.value)}
                className='w-8 h-8 rounded border border-default-300 '
                // style={{
                //   background: 'linear-gradient(90deg, red, orange, yellow, green, blue, indigo, violet)',
                //   WebkitAppearance: 'none', // hide default colour box (Chrome/Safari)
                //   MozAppearance: 'none', // hide default colour box (Firefox)
                // }}
              />
              <Button
                size='sm'
                variant='bordered'
                onPress={() => onBackgroundColorChange(customColor)}
                startContent={<MdColorLens className='size-6' />}
              >
                Use
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className='border-t border-default-200'>
        <h4 className='text-sm font-medium my-3'>Rotate & Flip</h4>
        <div className='grid grid-cols-4 gap-2'>
          <Button
            radius='sm'
            size='sm'
            className='py-4'
            // isIconOnly
            variant='bordered'
            title='Rotate Left'
            onPress={() => rotate(-90)}
            startContent={<MdRotateLeft className='size-6' />}
          >
            {/* Left */}
          </Button>
          <Button
            radius='sm'
            size='sm'
            className='py-4'
            // isIconOnly
            variant='bordered'
            title='Rotate Right'
            onPress={() => rotate(90)}
            startContent={<MdRotateRight className='size-6' />}
          >
            {/* Right */}
          </Button>
          <Button
            radius='sm'
            size='sm'
            className='py-4'
            // isIconOnly
            variant='bordered'
            title='Horizontal-Flip'
            onPress={() => flip(true, false)}
            startContent={<MdFlip className='size-6' />}
          >
            {/* H-Flip */}
          </Button>
          <Button
            radius='sm'
            size='sm'
            className='py-4'
            // isIconOnly
            variant='bordered'
            title='Vertical-Flip'
            onPress={() => flip(false, true)}
            startContent={<MdVerticalAlignCenter className='size-6' />}
          >
            {/* V-Flip */}
          </Button>
        </div>
      </div>

      {/* Crop Controls */}
      <div className='border-t border-default-200'>
        <h4 className='text-sm font-medium my-3'>Crop</h4>
        <div className='space-y-2'>
          {isCropping ? (
            <>
              <div className='gap-x-2 flex items-center justify-between'>
                <Button
                  // size='sm'
                  variant='solid'
                  // variant={isCropping ? 'solid' : 'bordered'}
                  color={'warning'}
                  onPress={() => setIsCropping(false)}
                  startContent={<BsCrop />}
                  className='w-full'
                >
                  Cancel Crop
                </Button>
                <Button
                  // size='sm'
                  variant='solid'
                  // variant={isCropping ? 'solid' : 'bordered'}
                  color={'success'}
                  onPress={onCropApply}
                  startContent={<BsCrop />}
                  className='w-full text-white'
                >
                  Apply Crop
                </Button>
              </div>
              <div className='mt-2 text-xs text-default-500'>
                <span>Scroll mouse wheel up or down to zoom in or out.</span>
                <div>
                  You can move the image by clicking and dragging outside the crop area with your mouse.
                </div>
              </div>
            </>
          ) : (
            <Button
              // size='sm'
              variant='solid'
              // variant={isCropping ? 'solid' : 'bordered'}
              color={'default'}
              onPress={() => setIsCropping(true)}
              startContent={<BsCrop />}
              className='w-full'
            >
              Start Crop
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageTransformControls;

/* <Button
                size='sm'
                variant='bordered'
                onPress={onZoomIn}
                startContent={<GoZoomIn className='size-6' />}
              >
                Zoom In
              </Button>
              <Button
                size='sm'
                variant='bordered'
                onPress={onZoomOut}
                startContent={<GoZoomOut className='size-6' />}
              >
                Zoom Out
              </Button>
              <Slider
                aria-label='Zoom'
                color='foreground'
                maxValue={3}
                defaultValue={0}
                step={0.1}
                value={absoluteZoom}
                onChange={(value) => onZoom(value as number)}
              /> */
